import { createContext, useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api from "../lib/api";

// AppModeContext — single source of truth for "is feature X visible?".
// Powered by GET /api/config which reads APP_MODE on the backend and applies
// any URL-based override (?mode=demo or /demo prefix).
//
// Three modes:
//   production → only features marked live_ready: true are visible
//   staging    → everything visible, yellow banner
//   demo       → everything visible, blue banner, "no real money" disclaimer
//
// We keep mode resolution server-side so a single deployment can serve
// production at "/" and demo at "/demo" with the SAME bundle.
const AppModeCtx = createContext({
  mode: "production", banner: { show: false }, features: {}, loaded: false,
  isVisible: () => true,
});

export function AppModeProvider({ children }) {
  const location = useLocation();
  const [state, setState] = useState({ mode: "production", banner: { show: false }, features: {}, loaded: false });

  // /demo prefix forces demo mode; ?mode=staging works too (for embedded preview).
  // sessionStorage override persists across login redirects so demo users
  // can land on /buyer and still see demo mode.
  const sessionOverride = typeof window !== "undefined"
    ? sessionStorage.getItem("app_mode_override")
    : null;
  const urlMode = location.pathname.startsWith("/demo")
    ? "demo"
    : new URLSearchParams(location.search).get("mode") || sessionOverride;

  useEffect(() => {
    let cancelled = false;
    const params = urlMode ? { mode: urlMode } : {};
    api.get("/config", { params })
      .then(r => {
        if (cancelled) return;
        setState({
          mode: r.data.app_mode,
          banner: r.data.banner || { show: false },
          features: r.data.features || {},
          loaded: true,
        });
      })
      .catch(() => {
        if (cancelled) return;
        // Fail closed: assume production (most restrictive). Better to hide
        // a working feature than to leak a mock one to a paying customer.
        setState(s => ({ ...s, loaded: true }));
      });
    return () => { cancelled = true; };
  }, [urlMode]);

  const isVisible = (featureId) => {
    // While loading we hide everything (avoids a flash of mock content
    // before the config response arrives in production).
    if (!state.loaded) return false;
    // Unknown feature ids default to visible — opt-in gating only for
    // features explicitly listed in the registry.
    if (!(featureId in state.features)) return true;
    return !!state.features[featureId];
  };

  return (
    <AppModeCtx.Provider value={{ ...state, isVisible }}>
      {children}
    </AppModeCtx.Provider>
  );
}

export function useAppMode() {
  return useContext(AppModeCtx);
}

// Convenience helper for menu items: pass the array, get back the same
// array filtered to currently-visible items.
export function filterByFeature(items, isVisible) {
  return (items || []).filter(it => !it.feature || isVisible(it.feature));
}
